import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Cable, Plug, Gauge, Caravan, BookOpen } from "lucide-react";
import { SERVICES } from "../../data/services";

const TOWING_SLUGS = [
  { slug: "redarc-brake-controllers", icon: Gauge },
  { slug: "12-pin-trailer-plug-install", icon: Cable },
  { slug: "anderson-plug-installation-sunshine-coast", icon: Plug },
  { slug: "caravan-tow-vehicle-electrical-sunshine-coast", icon: Caravan },
];

const GUIDES = [
  { slug: "what-brake-controller-do-i-need", title: "What brake controller do I need?" },
  { slug: "300-series-landcruiser-tow-pro-install", title: "300 Series LandCruiser Tow-Pro install guide" },
];

export default function TowingHub() {
  const cards = TOWING_SLUGS.map((t) => ({ ...t, service: SERVICES.find((s) => s.slug === t.slug) })).filter((c) => c.service);
  return (
    <section
      id="towing"
      data-testid="towing-hub-section"
      className="relative bg-[#0A0A0A] border-y border-cyan-pz/20 py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grain" aria-hidden="true" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">OUR SPECIALTY</span>
            </div>
            <h2 className="bebas text-white text-5xl md:text-7xl leading-[0.9]">
              TOWING ELECTRICS <br />
              <span className="text-cyan-pz">SUNSHINE COAST.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-white/70 barlow text-base md:text-lg leading-relaxed">
              Caravans, camper trailers, boats, horse floats and car trailers — if it has electric brakes
              or needs charging on the move, we wire the tow vehicle for it. REDARC Tow-Pro Elite brake
              controllers, 12 pin trailer plugs and grey Anderson plugs are the jobs we do most, every week,
              at our Kunda Park workshop. Every install is tested on a trailer before you leave.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {cards.map(({ slug, icon: Icon, service }) => (
            <Link
              key={slug}
              to={`/services/${slug}`}
              data-testid={`towing-hub-${slug}`}
              className="group bg-black p-6 md:p-8 hover:bg-cyan-pz/5 transition-colors flex flex-col"
            >
              <Icon className="w-7 h-7 text-cyan-pz mb-5" />
              <h3 className="bebas text-white text-2xl leading-tight mb-2 group-hover:text-cyan-pz transition-colors">
                {service.name}
              </h3>
              <p className="text-white/55 barlow text-sm leading-relaxed flex-1">{service.kicker}</p>
              {service.priceFrom && (
                <p className="mt-4 bebas text-cyan-pz text-2xl tracking-wider">
                  {(String(service.priceFrom).toLowerCase().startsWith("from") || !/\$/.test(service.priceFrom)
                    ? service.priceFrom
                    : `From ${service.priceFrom}`) + (slug === "12-pin-trailer-plug-install" ? "*" : "")}
                </p>
              )}
              <span className="mt-4 inline-flex items-center gap-2 bebas text-white/70 tracking-widest text-sm group-hover:text-cyan-pz transition-colors">
                VIEW PRICING
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <Link
            to="/towing-deals"
            data-testid="towing-hub-package-link"
            className="inline-flex items-center justify-center gap-3 border border-cyan-pz text-cyan-pz px-8 py-4 bebas text-lg tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors self-start"
          >
            TOW READY PACKAGE
            <ArrowUpRight className="w-5 h-5" />
          </Link>
          <Link
            to="/services/towing-electrics-sunshine-coast"
            data-testid="towing-hub-main-link"
            className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-4 bebas text-lg tracking-[0.2em] hover:bg-white transition-colors clip-perf self-start"
          >
            ALL TOWING ELECTRICS SERVICES
            <ArrowUpRight className="w-5 h-5" />
          </Link>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm barlow">
            <span className="text-white/40 inline-flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-cyan-pz/70" /> Guides:
            </span>
            {GUIDES.map((g) => (
              <Link
                key={g.slug}
                to={`/blog/${g.slug}`}
                data-testid={`towing-hub-guide-${g.slug}`}
                className="text-white/70 hover:text-cyan-pz underline-offset-4 hover:underline transition-colors"
              >
                {g.title}
              </Link>
            ))}
          </div>
        </div>
        <p className="mt-6 text-white/40 barlow text-xs" data-testid="towing-hub-12pin-disclaimer">
          * 12 pin price shown is for a basic 12 pin plug install. Additional charges may apply if extra pins or circuits are required.
          A current caravan / trailer wiring diagram is required before any 12 pin or Anderson plug booking.
        </p>
      </div>
    </section>
  );
}
